import {Component, Type, ViewChild, ViewContainerRef} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {FooterComponent} from '../components/footer/footer.component';
import {HeaderComponent} from '../components/header/header.component';
import {IBaseComponent} from '../../../sdk/src/components/ibase/ibase.component';
import {MegaMenuItem, MenuItem} from 'primeng/api';
import {ECpmCRouteTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {CpmCUserRoleApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {CpmCCustomerUserMembershipApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer-user-membership.api-service';
import {CpmCOrganizationApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-organization.api-service';
import {Subscription} from 'rxjs';
import {PrimeNgImportsModule} from '../../../sdk/src/prime-ng-imports';
import {CustomerUrlBuilderUtil} from '../utils/customer-url-builder.util';
import {CpmCObjectStoreEntryProxyApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCFileApiService} from '../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {CpmCProjectApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-project.api-service';
import {CpmCServiceRequestGroupApiService} from '../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-service-request-group.api-service';

/**
 * Customer layout component
 */
@Component({
    selector: 'app-customer-layout',
    imports: [PrimeNgImportsModule, RouterOutlet, FooterComponent, HeaderComponent],
    templateUrl: './customer-layout.component.html',
    styleUrls: ['./customer-layout.component.scss']
})
export class CustomerLayoutComponent extends IBaseComponent {
    @ViewChild(HeaderComponent, {static: true})
    public header?: HeaderComponent;

    // ===============================================================
    // ===============================================================

    @ViewChild('viewContainerRef', {read: ViewContainerRef})
    protected viewContainerRef?: ViewContainerRef;

    protected sideComponent: Type<unknown> | null = null;
    protected sideBottomPadding = true;

    // ===============================================================
    // ===============================================================

    private isRouteContextReadySubscription: Subscription | undefined = undefined;

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        if (this.globalsService.sessionService.getIsRouteContextReady()) this.header?.setMenuItems(this.getMenuItems());

        this.isRouteContextReadySubscription = this.globalsService.sessionService.isRouteContextReadySubject.subscribe(
            () => {
                this.header?.setMenuItems(this.getMenuItems());
            }
        );
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */

    protected override async onDestroy(): Promise<void> {
        if (this.isRouteContextReadySubscription) this.isRouteContextReadySubscription.unsubscribe();
    }

    // ===============================================================
    // ===============================================================

    /**
     * Set side component (left panel)
     * @param component New component type
     */
    public setSideComponent(component: Type<unknown> | null = null, sideBottomPadding = true) {
        this.sideComponent = component;
        this.sideBottomPadding = sideBottomPadding;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init menu items
     */
    private getMenuItems(): MegaMenuItem[] {
        const customer = this.globalsService.sessionService.getCustomer();

        const menuItems: MegaMenuItem[] = [
            {
                label: customer?.name,
                disabled: true,
                styleClass: 'app-customer-menu-label'
            }
        ];

        menuItems.push({
            label: 'Service Requests',
            routerLink: [CustomerUrlBuilderUtil.customerServiceRequests(customer?.uuid)]
        });

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCOrganizationApiService.endpointList(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            menuItems.push({
                label: 'Organizations',
                routerLink: [CustomerUrlBuilderUtil.customerOrganizations(customer?.uuid)]
            });
        }

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCProjectApiService.endpointList(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            menuItems.push({
                label: 'Projects',
                routerLink: [CustomerUrlBuilderUtil.customerProjects(customer?.uuid)]
            });
        }

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCServiceRequestGroupApiService.endpointList(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            menuItems.push({
                label: 'Groups',
                routerLink: [CustomerUrlBuilderUtil.customerGroups(customer?.uuid)]
            });
        }

        const settingsMegaMenu = this.getSettingsMegaMenuItem();
        if (settingsMegaMenu?.items?.length && settingsMegaMenu?.items?.length > 0) menuItems.push(settingsMegaMenu);

        return menuItems;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Build settings mega menu item
     * @returns Settings mega menu item
     */
    private getSettingsMegaMenuItem(): MegaMenuItem {
        const customer = this.globalsService.sessionService.getCustomer();
        const result: MegaMenuItem = {
            label: 'Settings',
            items: [],
            appLogicalRouterLink: CustomerUrlBuilderUtil.settings(customer?.uuid)
        };

        const colA: MenuItem[] = [];
        const colB: MenuItem[] = [];

        // ===============================================================

        const permissionsGroup: MenuItem = {
            label: 'Permissions',
            items: []
        };
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCUserRoleApiService.endpointList(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            permissionsGroup.items?.push({
                label: 'User Roles',
                routerLink: [CustomerUrlBuilderUtil.settingsUserRoles(customer?.uuid)]
            });
        }
        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCCustomerUserMembershipApiService.endpointList(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            permissionsGroup.items?.push({
                label: 'Customer User Memberships',
                routerLink: [CustomerUrlBuilderUtil.settingsCustomerUserMemberships(customer?.uuid)]
            });
        }

        if (permissionsGroup.items?.length && permissionsGroup.items?.length > 0) colA.push(permissionsGroup);

        // ===============================================================

        const configurationGroup: MenuItem = {
            label: 'Configuration',
            items: []
        };

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCObjectStoreEntryProxyApiService.endpointList(this.FAKE_UUID, 'placeholder', this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            configurationGroup.items?.push({
                label: 'Object Store',
                routerLink: [CustomerUrlBuilderUtil.settingsObjectStore(customer?.uuid)],
                routerLinkActiveOptions: {exact: true}
            });
        }

        if (
            this.globalsService.sessionService.isAuthorizedForRouteResource(
                CpmCFileApiService.endpointListUserFiles(this.FAKE_UUID),
                ECpmCRouteTypeApiEnum.READ
            )
        ) {
            configurationGroup.items?.push({
                label: 'Files',
                routerLink: [CustomerUrlBuilderUtil.settingsFiles(customer?.uuid)]
            });
        }

        if (configurationGroup.items?.length && configurationGroup.items?.length > 0) colB.push(configurationGroup);

        // ===============================================================

        if (colA.length > 0) result.items?.push(colA);
        if (colB.length > 0) result.items?.push(colB);

        return result;
    }
}
